import type { Branded } from '#layers/base/public';
import { getKeyId } from '#layers/access/utils/get-key-id';

/**
 * The single home for the entitlement wire format: `resource:action[:resourceId]`.
 * The policy editor and the permissions store both read and write grants through these
 * helpers, so the format keeps one definition.
 */

/** The id of one resource row in a grant key. Minted only by {@link toEntitlementResourceId} and the decoder. */
export type EntitlementResourceId = Branded<string, 'EntitlementResourceId'>;

/** What a grant or request applies to within its `resource:action`. */
export const EntitlementTargetKinds = {
	/** The action itself, for actions that are not per resource (`resource:action`). */
	Unscoped: 'unscoped',
	/** Every resource (`resource:action:*`); as a request, "at least one resource". */
	AnyResource: 'any',
	/** One resource (`resource:action:<id>`). */
	OneResource: 'one',
} as const;

export type EntitlementTarget =
	| { kind: typeof EntitlementTargetKinds.Unscoped }
	| { kind: typeof EntitlementTargetKinds.AnyResource }
	| { kind: typeof EntitlementTargetKinds.OneResource; id: EntitlementResourceId };

export interface EntitlementKeyParts {
	resource: string;
	action: string;
	target: EntitlementTarget;
}

const WILDCARD = '*';

export const unscopedTarget: EntitlementTarget = { kind: EntitlementTargetKinds.Unscoped };
export const anyResourceTarget: EntitlementTarget = { kind: EntitlementTargetKinds.AnyResource };

function isEntitlementResourceId(raw: string): raw is EntitlementResourceId {
	return raw.length > 0 && raw !== WILDCARD;
}

/** The resource id of an item key, or `null` when the key has no scalar `id` to grant on. */
export function toEntitlementResourceId(key: Parameters<typeof getKeyId>[0]): EntitlementResourceId | null {
	const raw = getKeyId(key);
	return raw !== undefined && isEntitlementResourceId(raw) ? raw : null;
}

export function oneResourceTarget(id: EntitlementResourceId): EntitlementTarget {
	return { kind: EntitlementTargetKinds.OneResource, id };
}

/**
 * The target of a check on a scoped action: every resource without a key, that resource's id with
 * one. `null` when a key is given but has no id: the check must fail rather than widen to "any".
 */
export function targetForKey(key: Parameters<typeof getKeyId>[0]): EntitlementTarget | null {
	if (key === undefined) return anyResourceTarget;

	const id = toEntitlementResourceId(key);
	return id ? oneResourceTarget(id) : null;
}

function encodeResourceAction(resource: string, action: string): string {
	return `${resource}:${action}`;
}

/** Build a grant key. The sole writer of the wire format. */
export function encodeEntitlementKey({ resource, action, target }: EntitlementKeyParts): string {
	const base = encodeResourceAction(resource, action);

	switch (target.kind) {
		case EntitlementTargetKinds.Unscoped:
			return base;
		case EntitlementTargetKinds.AnyResource:
			return `${base}:${WILDCARD}`;
		case EntitlementTargetKinds.OneResource:
			return `${base}:${target.id}`;
		default: {
			const unhandled: never = target;
			return unhandled;
		}
	}
}

/**
 * Parse a grant key leniently: never throws; missing segments come back as `''`. The first two
 * segments are resource and action; everything after the second `:` is the resource id verbatim,
 * so a colon-bearing id (`a:b`, an item primary key) survives the round-trip. A key with only two
 * segments, or an empty id segment, is unscoped.
 */
export function decodeEntitlementKey(key: string): EntitlementKeyParts {
	const [resource = '', action = '', ...rest] = key.split(':');
	const raw = rest.join(':');

	if (rest.length === 0 || raw.length === 0) return { resource, action, target: unscopedTarget };
	if (raw === WILDCARD) return { resource, action, target: anyResourceTarget };

	return isEntitlementResourceId(raw)
		? { resource, action, target: oneResourceTarget(raw) }
		: { resource, action, target: unscopedTarget };
}

/**
 * Whether a grant key belongs to a given `(resource, action)` row. Delimiter-anchored, so
 * action `read` does not match a `read_only` grant and resource `users` does not match a
 * `users_extended` grant (a bare `startsWith` over-grants both).
 */
export function entitlementKeyMatchesAction(
	key: string,
	{ resource, action }: Pick<EntitlementKeyParts, 'resource' | 'action'>,
): boolean {
	const prefix = encodeResourceAction(resource, action);
	return key === prefix || key.startsWith(`${prefix}:`);
}

/**
 * Drop every key equal to the encoded `parts`. Exact match (not prefix), so removing `:123`
 * keeps `:1234`.
 */
export function removeEntitlementKey(keys: string[], parts: EntitlementKeyParts): string[] {
	const exact = encodeEntitlementKey(parts);
	return keys.filter((key) => key !== exact);
}

export interface GrantIndex {
	keys: Set<string>;
	resourceActions: Set<string>;
}

/**
 * Index a user's merged grant set for O(1) `isGranted` lookups: every grant key, plus the
 * `resource:action` of every id-scoped grant (so an "any id" query can be answered).
 */
export function buildGrantIndex(grants: Iterable<string>): GrantIndex {
	const keys = new Set<string>();
	const resourceActions = new Set<string>();

	for (const grant of grants) {
		keys.add(grant);

		const { resource, action, target } = decodeEntitlementKey(grant);

		if (resource && action && target.kind !== EntitlementTargetKinds.Unscoped) {
			resourceActions.add(encodeResourceAction(resource, action));
		}
	}

	return { keys, resourceActions };
}

/**
 * Whether the grant set covers a request. Precedence: `resource:*` blanket → for an unscoped
 * request the exact `resource:action`; for one resource the exact id or the `:*` action wildcard;
 * for any resource the `:*` wildcard or any id-scoped grant on that action. An unscoped grant
 * never satisfies a scoped request. Admin bypass is the caller's concern.
 */
export function isGranted(index: GrantIndex, request: EntitlementKeyParts): boolean {
	const { resource, action, target } = request;
	const resourceAction = encodeResourceAction(resource, action);
	const actionWildcard = encodeEntitlementKey({ resource, action, target: anyResourceTarget });

	if (index.keys.has(encodeResourceAction(resource, WILDCARD))) return true;

	switch (target.kind) {
		case EntitlementTargetKinds.Unscoped:
			return index.keys.has(resourceAction);
		case EntitlementTargetKinds.AnyResource:
			return index.keys.has(actionWildcard) || index.resourceActions.has(resourceAction);
		case EntitlementTargetKinds.OneResource:
			return index.keys.has(encodeEntitlementKey(request)) || index.keys.has(actionWildcard);
		default: {
			const unhandled: never = target;
			return unhandled;
		}
	}
}

export const AllowedResourcesKinds = {
	All: 'all',
	Some: 'some',
} as const;

/** The resources a grant set allows for a scoped `resource:action`. */
export type AllowedResources =
	| { kind: typeof AllowedResourcesKinds.All }
	| { kind: typeof AllowedResourcesKinds.Some; ids: readonly EntitlementResourceId[] };

export const allResourcesAllowed: AllowedResources = { kind: AllowedResourcesKinds.All };
export const noResourcesAllowed: AllowedResources = { kind: AllowedResourcesKinds.Some, ids: [] };

export function allowsResource(allowed: AllowedResources, id: string): boolean {
	if (allowed.kind === AllowedResourcesKinds.All) return true;

	const ids: readonly string[] = allowed.ids;
	return ids.includes(id);
}

export function allowsAnyResource(allowed: AllowedResources): boolean {
	return allowed.kind === AllowedResourcesKinds.All || allowed.ids.length > 0;
}

/**
 * The resources a grant set allows for a scoped `resource:action`: all of them for a `resource:*`
 * blanket or a `resource:action:*` wildcard, otherwise the specific granted ids (possibly none).
 * Use to scope a picker to the assignable items. Admin is the caller's concern.
 */
export function allowedResourceIds(index: GrantIndex, resource: string, action: string): AllowedResources {
	const resourceAction = encodeResourceAction(resource, action);

	if (index.keys.has(encodeResourceAction(resource, WILDCARD)) || index.keys.has(encodeEntitlementKey({ resource, action, target: anyResourceTarget }))) {
		return allResourcesAllowed;
	}

	const ids: EntitlementResourceId[] = [];

	for (const key of index.keys) {
		const parts = decodeEntitlementKey(key);
		if (encodeResourceAction(parts.resource, parts.action) !== resourceAction) continue;
		if (parts.target.kind === EntitlementTargetKinds.OneResource) ids.push(parts.target.id);
	}

	return { kind: AllowedResourcesKinds.Some, ids };
}
